// Typed client + TanStack Query hooks for erp-core `internal/periods`
// (routes in periods/handler.go) and the two exits of the closed-period
// dialog. Reads are manager+; close / reopen are owner (the server enforces;
// the screen only hides). Writes are never retried (409 rule) and carry
// `meta.periodClosed = 'manual'` so the global handler does not double-fire.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/lib/api';
import { baghdadToday } from '@/lib/format';
import type { Checklist, JournalEntry, Period, PeriodDetail, PeriodList, ReopenRequest } from './types';

export const periodsApi = {
  list: () => erpApi.get<PeriodList>('/periods'),
  get: (month: string) => erpApi.get<PeriodDetail>(`/periods/${month}`),
  checklist: (month: string) => erpApi.get<Checklist>(`/periods/${month}/close-checklist`),
  close: (month: string) => erpApi.post<Period>(`/periods/${month}/close`),
  reopen: (month: string, reason: string) => erpApi.post<Period>(`/periods/${month}/reopen`, { reason }),
  /** staff+ — «طلب إعادة الفتح» to the owner (201; 200 + already_requested when one is pending). */
  requestReopen: (month: string, reason: string) => erpApi.post<ReopenRequest>(`/periods/${month}/reopen-requests`, { reason }),
  /** manager+ — the owner's inbox. */
  reopenRequests: (q: { month?: string; state?: 'pending' | 'approved' } = {}) =>
    erpApi.get<{ requests: ReopenRequest[]; total: number }>('/periods/reopen-requests', q as Record<string, string>),
};

export const journalApi = {
  /** journal.Reverse: a mirrored `reversal` entry dated `date` (never a mutation of the original). */
  reverse: (entryId: string, date: string = baghdadToday()) => erpApi.post<JournalEntry>(`/journal/${entryId}/reverse`, { date }),
};

export const periodKeys = {
  all: ['erp', 'periods'] as const,
  list: () => [...periodKeys.all, 'list'] as const,
  detail: (month: string) => [...periodKeys.all, 'detail', month] as const,
  checklist: (month: string) => [...periodKeys.all, 'checklist', month] as const,
  requests: (month: string) => [...periodKeys.all, 'reopen-requests', month] as const,
};

/** Pending reopen requests of a month (manager+ read; the close page lists them). */
export function useReopenRequests(month: string | null, enabled = true) {
  return useQuery({
    queryKey: periodKeys.requests(month ?? ''),
    queryFn: () => periodsApi.reopenRequests({ month: month as string, state: 'pending' }),
    enabled: enabled && !!month,
  });
}

export function useRequestReopen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, reason }: { month: string; reason: string }) => periodsApi.requestReopen(month, reason),
    meta: { periodClosed: 'manual' } as const,
    onSettled: () => qc.invalidateQueries({ queryKey: periodKeys.all }),
  });
}

export function usePeriods() {
  return useQuery({ queryKey: periodKeys.list(), queryFn: periodsApi.list });
}

export function usePeriodDetail(month: string | null, enabled = true) {
  return useQuery({
    queryKey: periodKeys.detail(month ?? ''),
    queryFn: () => periodsApi.get(month as string),
    enabled: enabled && !!month,
  });
}

export function useChecklist(month: string | null, enabled = true) {
  return useQuery({
    queryKey: periodKeys.checklist(month ?? ''),
    queryFn: () => periodsApi.checklist(month as string),
    enabled: enabled && !!month,
  });
}

const MANUAL = { periodClosed: 'manual' } as const;

export function useClosePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => periodsApi.close(month),
    meta: MANUAL,
    onSettled: () => qc.invalidateQueries({ queryKey: ['erp'] }),
  });
}

export function useReopenPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, reason }: { month: string; reason: string }) => periodsApi.reopen(month, reason),
    meta: MANUAL,
    onSettled: () => qc.invalidateQueries({ queryKey: ['erp'] }),
  });
}

/**
 * Pick the month the screen opens on when the URL names none: the oldest
 * open month that has ended (the next one to close), else the latest closed
 * month, else the newest month the server lists. Never invents a month.
 */
export function defaultMonth(list: PeriodList): string | null {
  const asc = [...list.periods].sort((a, b) => a.month.localeCompare(b.month));
  const toClose = asc.find((p) => p.state === 'open' && p.ended);
  if (toClose) return toClose.month;
  const closed = asc.filter((p) => p.state === 'closed');
  if (closed.length > 0) return closed[closed.length - 1].month;
  return asc.length > 0 ? asc[asc.length - 1].month : null;
}

/** The chip window: `size` months around `selected`, newest first (board: 3 chips). */
export function chipWindow(months: string[], selected: string, size = 3): string[] {
  const desc = [...new Set(months)].sort((a, b) => b.localeCompare(a));
  if (!desc.includes(selected)) desc.push(selected);
  desc.sort((a, b) => b.localeCompare(a));
  const i = desc.indexOf(selected);
  let start = Math.max(0, i - Math.floor((size - 1) / 2));
  if (i === 0) start = 0;
  start = Math.min(start, Math.max(0, desc.length - size));
  return desc.slice(start, start + size);
}
